
import React from "react";
import { Building, MapPin, Users, FileText, Globe, Twitter, Instagram, Linkedin } from "lucide-react";
import { Control } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { CompanyFormValues, australianLocations } from "./CompanyFormSchema";

interface CompanyFormFieldsProps {
  control: Control<CompanyFormValues>;
}

export const CompanyNameField = ({ control }: CompanyFormFieldsProps) => {
  return (
    <FormField
      control={control}
      name="name"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="flex items-center gap-2">
            <Building className="w-4 h-4" />
            Company Name
          </FormLabel>
          <FormControl>
            <Input placeholder="Enter company name" {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const LocationField = ({ control }: CompanyFormFieldsProps) => {
  return (
    <FormField
      control={control}
      name="location"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Location
          </FormLabel>
          <FormControl>
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select company location" />
              </SelectTrigger>
              <SelectContent>
                {australianLocations.map((location) => (
                  <SelectItem key={location} value={location}>
                    {location}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const BioField = ({ control }: CompanyFormFieldsProps) => {
  return (
    <FormField
      control={control}
      name="bio"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Company Bio
          </FormLabel>
          <FormControl>
            <Textarea 
              placeholder="Brief description of the company" 
              className="min-h-32" 
              {...field} 
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const StaffSizeField = ({ control }: CompanyFormFieldsProps) => {
  return (
    <FormField
      control={control}
      name="staffSize"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Staff Size
          </FormLabel>
          <FormControl>
            <Input placeholder="e.g., 1-10, 11-50, 51-200, 200+" {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const WebsiteField = ({ control }: CompanyFormFieldsProps) => {
  return (
    <FormField
      control={control}
      name="website"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Website URL
          </FormLabel>
          <FormControl>
            <Input placeholder="https://example.com" {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const TwitterField = ({ control }: CompanyFormFieldsProps) => {
  return (
    <FormField
      control={control}
      name="twitterUrl"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="flex items-center gap-2">
            <Twitter className="w-4 h-4" />
            X (Twitter) URL
          </FormLabel>
          <FormControl>
            <Input placeholder="https://x.com/companyname" {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const InstagramField = ({ control }: CompanyFormFieldsProps) => {
  return (
    <FormField
      control={control}
      name="instagramUrl"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="flex items-center gap-2">
            <Instagram className="w-4 h-4" />
            Instagram URL
          </FormLabel>
          <FormControl>
            <Input placeholder="https://instagram.com/companyname" {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const LinkedinField = ({ control }: CompanyFormFieldsProps) => {
  return (
    <FormField
      control={control}
      name="linkedinUrl"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="flex items-center gap-2">
            <Linkedin className="w-4 h-4" />
            LinkedIn URL
          </FormLabel>
          <FormControl>
            <Input placeholder="https://linkedin.com/company/companyname" {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
